import React from "react";
import ProvidersFormDialogProvidersForm from "./ProvidersFormDialogProvidersForm";
import ProvidersFormDialogROIForm from "./ProvidersFormDialogROIForm";
import MultiStepFormDialog from "../../Widgets/MultiStepFormDialog/MultiStepFormDialog";
import useProvidersFormDialogMutations from "./useProvidersFormDialogMutations";
import { checkboxes, providerTypes } from "./providerTypes";
import { useGlobalToast } from "components/GlobalToastProvider";

export const ProviderFormDialogType = {
  newProvider: "newProvider",
  roi: "roi",
  updateProvider: "updateProvider",
};

interface ProviderFormDialogProps {
  open: boolean;
  closeDialog: () => void;
  formDialogType: string;
  clientId: string;
  frequencyOfCareCoordination?: string;
  providerFormValues?: any;
  onProviderChanges: (ROICreated?: boolean) => void;
  userPermissions?: any;
}

const ProviderFormDialog: React.FC<ProviderFormDialogProps> = ({
  open,
  closeDialog,
  formDialogType,
  clientId,
  frequencyOfCareCoordination = "",
  providerFormValues,
  onProviderChanges,
  userPermissions,
}) => {
  const {
    createUnsignedRequestMutation,
    linkPhysicianMutation,
    updatePhysicianMutation,
    createPhysicianMutation,
  } = useProvidersFormDialogMutations();
  const { showToast } = useGlobalToast();

  const setROIDefaultValues = () => {
    const defaultDisclosures: any = {};
    const defaults =
      providerTypes.find((p: any) => p.value === providerFormValues?.type)?.defaultDisclosureInfo ||
      [];

    defaults.forEach((field: string) => {
      defaultDisclosures[field] = true;
    });

    const nonDefaults = checkboxes.filter((c: string) => !defaults.includes(c));
    nonDefaults.forEach((field: string) => {
      defaultDisclosures[field] = false;
    });

    defaultDisclosures["otherDescription"] = "";
    return defaultDisclosures;
  };

  const handleSendReleaseForm = async (values: any) => {
    const target = providerFormValues?.teamMemberId !== undefined &&
      providerFormValues?.teamMemberId !== null
      ? {
          teamMemberId: providerFormValues.teamMemberId,
          teamMemberUserId: providerFormValues.teamMemberUserId,
        }
      : { provider: providerFormValues };
    const result = await createUnsignedRequestMutation.mutateAsync({
      ...values,
      ...target,
      clientId,
    });

    onProviderChanges(true);
    return result;
  };

  /**
   * Mirrors the Provider Library's create payload (src/components/Providers/index.tsx `onSave`):
   * the backend's createProvider service already creates a private-practice Clinic from the
   * address fields, or links the selected existing clinics, so the same shape is reused here.
   */
  const buildNewProviderPayload = (provider: any) => {
    const payload: any = {
      first_name: provider.firstName,
      last_name: provider.lastName,
      email: provider.email,
      phone: provider.phone,
      fax_number: provider.faxNumber || "",
      npi_number: provider.npiNumber || "",
      medicaid_id: provider.medicaidId || "",
      specialty: provider.specialty || "",
      accepts_insurance: Boolean(provider.acceptsInsurance),
    };

    if (provider.privatePractice) {
      payload.private_practice = true;
      payload.physical_address = [provider.physicalAddress, provider.physicalAddress2]
        .filter(Boolean)
        .join(" | ");
      payload.city = provider.city;
      payload.state = provider.state;
      payload.zip_code = provider.zipCode;
      payload.website = provider.website;
    } else if (provider.physicianClinic?.length) {
      payload.physician_clinic = provider.physicianClinic.map((item: any) => ({
        clinic_id: item.clinic_id,
      }));
    }

    return payload;
  };

  const createProviderIfNeeded = async (provider: any): Promise<string> => {
    if (provider.id !== "new") return provider.id;
    const createResponse: any = await createPhysicianMutation.mutateAsync(
      buildNewProviderPayload(provider)
    );
    const id = createResponse?.data?.id;
    if (!id) {
      throw new Error("Failed to create provider: missing id in response");
    }
    return id;
  };

  const handleSendReleaseAndAddProvider = async (values: any) => {
    const physicianId = await createProviderIfNeeded(values.provider);

    await createUnsignedRequestMutation.mutateAsync({
      ...values,
      provider: { ...values.provider, id: physicianId },
      clientId,
    });

    return { physicianId, values };
  };

  const handleLinkProvider = async (physicianId: string, values: any) => {
    await linkPhysicianMutation.mutateAsync({
      provider: {
        ...values.provider,
        id: physicianId,
        frequency_care_coordination:
          (values.provider?.frequencyCareCoordination ?? frequencyOfCareCoordination) || null,
      },
      clientId,
    });
  };

  const handleAddProvider = async (values: any) => {
    const physicianId = await createProviderIfNeeded(values.provider);

    const result = await linkPhysicianMutation.mutateAsync({
      ...values,
      provider: {
        ...values.provider,
        id: physicianId,
        frequency_care_coordination:
          (values.provider?.frequencyCareCoordination ?? frequencyOfCareCoordination) || null,
      },
      clientId,
    });

    onProviderChanges();
    return result;
  };

  const mapProviderToApi = (provider: any) => {
    const { isReferringProvider, frequencyCareCoordination, ...rest } = provider;
    return {
      ...rest,
      is_referring_provider: isReferringProvider ?? false,
      frequency_care_coordination: (frequencyCareCoordination ?? frequencyOfCareCoordination) || null,
    };
  };

  const handleUpdateProvider = async (values: any) => {
    try {
      const result = await updatePhysicianMutation.mutateAsync({
        provider: mapProviderToApi(values.provider),
        previousProviderType: providerFormValues?.type,
        clientId,
      });

      onProviderChanges();
      return result;
    } catch (error: any) {
      if (error?.response?.status === 409) {
        const providerTypeLabel = providerTypes.find(
          ({ value }) => value === values.provider?.type
        )?.label;
        const message = providerTypeLabel
          ? `Only one ${providerTypeLabel.toLowerCase()} can be linked to this client.`
          : "Only one provider of this type can be linked to this client.";

        showToast({ message, errorState: true });
      }
      throw error;
    }
  };

  const getDefaultValues = () => {
    if (formDialogType === ProviderFormDialogType.updateProvider) {
      return {
        provider: providerFormValues,
        isEdit: true,
        checkboxes: null,
      };
    }
    if (formDialogType === ProviderFormDialogType.roi) {
      return {
        ...setROIDefaultValues(),
        teamMemberId: providerFormValues?.teamMemberId,
        teamMemberUserId: providerFormValues?.teamMemberUserId,
        document: { title: "" },
        checkboxes: null,
      };
    }
    // Default for newProvider
    return {
      provider: {
        type: "",
        id: "",
        frequencyCareCoordination: frequencyOfCareCoordination,
        firstName: "",
        lastName: "",
        medicaidId: "",
        npiNumber: "",
        specialty: "",
        acceptsInsurance: false,
        email: "",
        phone: "",
        faxNumber: "",
        privatePractice: false,
        physicalAddress: "",
        physicalAddress2: "",
        city: "",
        state: "",
        zipCode: "",
        website: "",
        physicianClinic: [],
      },
      document: { title: "" },
      checkboxes: null,
    };
  };

  const getDialogConfig = () => {
    switch (formDialogType) {
      case ProviderFormDialogType.newProvider: {
        // If user has permission to create ROI, show both provider and ROI forms
        const newProviderSteps = userPermissions?.create_phi_release_request
          ? [ProvidersFormDialogProvidersForm, ProvidersFormDialogROIForm]
          : [ProvidersFormDialogProvidersForm];

        return {
          title: "Add Provider",
          steps: newProviderSteps,
          submitLabel: userPermissions?.create_phi_release_request
            ? "Send Release Form"
            : "Finish and Add Provider",
          onSubmit: userPermissions?.create_phi_release_request
            ? handleSendReleaseAndAddProvider
            : handleAddProvider,
          successMessage: userPermissions?.create_phi_release_request
            ? {
                title: "Release of Information successfully sent!",
                description: "The form will be available once the client has signed the release.",
              }
            : undefined,
          confirmAction: userPermissions?.create_phi_release_request
            ? {
                label: "Finish And Add Provider",
                handler: handleLinkProvider,
              }
            : undefined,
        };
      }

      case ProviderFormDialogType.updateProvider:
        return {
          title: "Edit Provider",
          steps: [ProvidersFormDialogProvidersForm],
          submitLabel: "Save Changes",
          onSubmit: handleUpdateProvider,
        };

      case ProviderFormDialogType.roi:
        return {
          title: "Create Release of Information Form",
          steps: [ProvidersFormDialogROIForm],
          submitLabel: "Send Release Form",
          onSubmit: handleSendReleaseForm,
          successMessage: {
            title: "Release of Information successfully sent!",
            description: "The form will be available once the client has signed the release.",
          },
        };

      default:
        return {
          title: "",
          steps: [],
          onSubmit: async () => {},
        };
    }
  };

  const config = getDialogConfig();

  return (
    <MultiStepFormDialog
      open={open}
      title={config.title}
      steps={config.steps}
      submitLabel={config.submitLabel}
      onClose={closeDialog}
      defaultValues={getDefaultValues()}
      onSubmit={config.onSubmit}
      successMessage={config.successMessage}
      confirmAction={config.confirmAction}
      onConfirmComplete={() => onProviderChanges()}
    />
  );
};

export default ProviderFormDialog;
